import { Navigate, useParams } from 'react-router-dom'
import { StateBlock } from '@w7/ui'
import { useSession } from '@/api/hooks/shared/useSession'
import { NetExposurePage } from '@/features/net-exposure/NetExposurePage'
import { BookManagementAccessGate } from './BookManagementAccessGate'
import { canOpenMode, currentLegacyProfile, landingMode } from './accessLevel'
import { resolveDataSource } from './dataSource'
import { parseWorkspaceMode, workspaceById, type ModeDescriptor } from './routes'
import { WORKSPACE_BOOKS_SCOPE, type BookWorkspaceId } from './domain'
import { TickerView } from './ticker/TickerView'
import { WorkspaceShell } from './WorkspaceShell'

function ModeBody({
  workspaceId,
  mode,
}: {
  workspaceId: BookWorkspaceId
  mode: ModeDescriptor
}) {
  if (mode.surface === 'ticker') {
    return <TickerView workspace={workspaceId} mode={mode} />
  }
  return <NetExposurePage embedded productScope={WORKSPACE_BOOKS_SCOPE[workspaceId]} />
}

/**
 * A workspace mode.
 *
 * The rail, the tabs and this route all ask the same question of the same
 * profile, so a page that is not offered is also not reachable by typing its
 * URL — the console this replaces hid links but left the pages open.
 */
export function WorkspacePage({ workspaceId }: { workspaceId: BookWorkspaceId }) {
  const workspace = workspaceById(workspaceId)
  const { mode: raw } = useParams()
  const { data: session } = useSession()
  const profile = currentLegacyProfile(session)
  const landing = landingMode(workspaceId, profile)

  if (!raw) {
    if (!landing) {
      return (
        <BookManagementAccessGate>
          <StateBlock icon="lock" title="Not available" tone="error">
            {workspace.label} is not part of this console for your role.
          </StateBlock>
        </BookManagementAccessGate>
      )
    }
    return <Navigate to={`${workspace.pathPrefix}/${landing}`} replace />
  }

  // Merged into Bet Ticker Previous (Lab Betfair 7-day tickerBQ switch).
  if (raw === 'previous-new') {
    return <Navigate to={`${workspace.pathPrefix}/previous`} replace />
  }

  const mode = parseWorkspaceMode(workspaceId, raw)

  if (!mode) {
    return (
      <BookManagementAccessGate>
        <StateBlock icon="search" title="That page does not exist">
          Unknown {workspace.label} page.
        </StateBlock>
      </BookManagementAccessGate>
    )
  }

  if (!canOpenMode(workspaceId, mode.id, profile)) {
    return (
      <BookManagementAccessGate>
        <StateBlock icon="lock" title="Not available" tone="error">
          {mode.label} is not part of this console for your role.
        </StateBlock>
      </BookManagementAccessGate>
    )
  }

  return (
    <BookManagementAccessGate>
      <WorkspaceShell workspace={workspace} mode={mode} source={resolveDataSource()}>
        <ModeBody workspaceId={workspaceId} mode={mode} />
      </WorkspaceShell>
    </BookManagementAccessGate>
  )
}

export function BookManagementWorkspace() {
  return <WorkspacePage workspaceId="book-management" />
}

export function PremiumBmWorkspace() {
  return <WorkspacePage workspaceId="premium-bm" />
}
